

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { AUTH_API_BASE_URL as API_BASE_URL, authApi } from "../../LeadMng/config/api";

const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "Not assigned";
  try {
    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return "Invalid date";
  }
};

const getDateRange = (range) => {
  const today = new Date();
  const start = new Date();

  switch (range) {
    case "today":
      start.setHours(0, 0, 0, 0);
      return { start: start.toISOString(), end: today.toISOString() };
    case "weekly":
      start.setDate(today.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { start: start.toISOString(), end: today.toISOString() };
    case "monthly":
      start.setMonth(today.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      return { start: start.toISOString(), end: today.toISOString() };
    default:
      return { start: null, end: null };
  }
};

const StatusBadge = ({ status }) => {
  const colors = {
    open: "bg-blue-100 text-blue-800",
    inprogress: "bg-yellow-100 text-yellow-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-300 text-gray-700",
    pending: "bg-orange-100 text-orange-800",
    reopen: "bg-blue-100 text-blue-800", // New: for reopened tickets
    assigned: "bg-green-100 text-green-800", // New: for initial assignment
    reassigned: "bg-yellow-100 text-yellow-800", // New: for reassignment
  };
  const colorClass = colors[status?.toLowerCase().replace(/\s/g, "")] || "bg-gray-100 text-gray-800";

  // Map status to display text and icon
  const statusInfo = {
    open: { icon: "🔓", text: "Open" },
    inprogress: { icon: "„", text: "In Progress" },
    resolved: { icon: "✅", text: "Resolved" },
    closed: { icon: "🔒", text: "Closed" },
    pending: { icon: "", text: "Pending" },
    reopen: { icon: "🔓", text: "Reopened" },
    assigned: { icon: "✅", text: "Assigned" }, // New
    reassigned: { icon: "„", text: "Reassigned" }, // New


  };
  const info = statusInfo[status?.toLowerCase().replace(/\s/g, "")] || { icon: "", text: status || "Unknown" };

  return (
    <span
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}
    >
      <span
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 5 }}
        className="mr-1"
      >
        {info.icon}
      </span>
      {info.text}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    high: "bg-red-100 text-red-800",
    medium: "bg-yellow-100 text-yellow-800",
    low: "bg-green-100 text-green-800",
  };
  const colorClass = colors[priority?.toLowerCase()] || "bg-gray-100 text-gray-800";

  return (
    <span
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}
    >
      <span
        animate={{
          scale: priority === "high" ? [1, 1.2, 1] : 1,
        }}
        transition={{ duration: 0.5, repeat: priority === "high" ? Infinity : 0, repeatDelay: 2 }}
        className="mr-1"
      >
        {priority === "high" && "🔥"}
        {priority === "medium" && "⚠️"}
        {priority === "low" && ""}
      </span>
      {priority ? `${priority} Priority` : "No Priority"}
    </span>
  );
};

const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, x: 100 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className={`fixed top-4 right-4 px-6 py-4 rounded-xl shadow-lg z-50 flex items-center ${type === "success" ? "bg-green-500" : "bg-red-500"
        } text-white`}
    >
      <span className="mr-2 text-xl">
        {type === "success" ? "✅" : "❌"}
      </span>
      <span>{message}</span>
      <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">
        ✅•
      </button>
    </motion.div>
  );
};

function AssignTickets() {
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [assignData, setAssignData] = useState({});
  const [notification, setNotification] = useState(null);

  // Search and sort states
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewMode] = useState("grid");

  // New filter states
  const [statusFilter, setStatusFilter] = useState("");
  const [materialFilter, setMaterialFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  // Date filter states
  const [dateRange, setDateRange] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [showAllMaterials, setShowAllMaterials] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);

  // Fetch tickets and related data
  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Access token not found.");

      const decodedToken = decodeToken(token);
      const companyId = decodedToken?.companyId || decodedToken?.id;

      if (!companyId) throw new Error("Company ID not found in token");
      setCompanyId(companyId);

      const [empRes, deptRes, ticketRes] = await Promise.all([
        fetch(`${API_BASE_URL}/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/departments/company/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/support-ticket`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      if (!empRes.ok) throw new Error(`Employees failed: ${empRes.status}`);
      if (!deptRes.ok) throw new Error(`Departments failed: ${deptRes.status}`);
      if (!ticketRes.ok) throw new Error(`Tickets failed: ${ticketRes.status}`);

      let empData = await empRes.json();
      const deptData = await deptRes.json();
      const ticketData = await ticketRes.json();
      const allTickets = ticketData.tickets || ticketData || [];

      empData = empData.filter((emp) => emp.company === companyId);

      const empMap = {};
      const empNameMap = {};
      empData.forEach((emp) => {
        empMap[emp._id] = emp;
        empNameMap[emp._id] = emp.teamMemberName;
      });

      const deptMap = {};
      const deptIdToName = {};
      deptData.forEach((dept) => {
        deptMap[dept._id] = dept;
        deptIdToName[dept._id] = dept.name;
      });

      const ticketsWithDetails = allTickets
        .map((ticket) => {
          const employee = empMap[ticket.customerId];

          // Current assignee name (from assignedTo)
          const assignedEmployeeName = ticket.assignedTo
            ? empNameMap[ticket.assignedTo] || "Unknown Employee"
            : null;

          // Populate assigneeHistory with names and formatted dates
          const assigneeHistoryWithDetails = (ticket.assigneeHistory || []).map((historyEntry) => ({
            ...historyEntry,
            employeeName: empNameMap[historyEntry.employeeId] || "Unknown Employee",
            assignedAtFormatted: formatDate(historyEntry.assignedAt), // Reuse your formatDate util
          }));

          const departmentId = employee ? employee.department : null;
          const departmentName = departmentId ? deptIdToName[departmentId] || "Unknown" : "Company";

          return {
            ...ticket,
            employeeName: employee ? employee.teamMemberName : "Company",
            departmentId,
            department: departmentName,
            company: ticket.company || companyId,
            assignedEmployee: ticket.assignedEmployee || "", // Deprecated, but keep for compatibility if needed
            assignedTo: ticket.assignedTo || null,
            assignedEmployeeName, // Current assignee
            assigneeHistory: assigneeHistoryWithDetails, // New: Populated history
            updatedAt: ticket.updatedAt || null,
            createdAt: ticket.createdAt || null,
            fieldWorkAssigned: ticket.fieldWorkAssigned || false,
            isAssigned: !!ticket.assignedTo, // Based on current assignedTo
            // Ensure these fields exist
            material: ticket.material || "",
            service: ticket.service || "",
            location: ticket.location || "",
            // Preserve reopenReason if it exists from backend
            reopenReason: ticket.reopenReason || "",
          };
        })
        .filter(
          (t) =>
            t.company === companyId && (empMap[t.customerId] || t.customerId === companyId)
        );


      // Add this sorting step right after the filter:
      const sortedTickets = [...ticketsWithDetails].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime() || 0;
        const dateB = new Date(b.createdAt).getTime() || 0;
        return dateB - dateA; // Descending: newest first
      });

      setEmployees(empData);
      setDepartments(deptData);
      setTickets(sortedTickets);  // Use sortedTickets here
      setFilteredTickets(sortedTickets);
    } catch (err) {
      console.error(err);
      setError("Error fetching data ❌");
      setNotification({ message: "Error fetching data", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Filter and sort tickets whenever dependencies change
  useEffect(() => {
    let temp = tickets;

    // Filter by department
    if (selectedDepartment) {
      temp = temp.filter((t) => t.department === selectedDepartment);
    }

    // Filter by tab
    if (activeTab === "assigned") {
      temp = temp.filter((t) => t.isAssigned);
    } else if (activeTab === "unassigned") {
      temp = temp.filter((t) => !t.isAssigned);
    }

    // Filter by status
    if (statusFilter) {
      temp = temp.filter((t) => t.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    // ✅ Material + Service filter logic
    if (materialFilter || serviceFilter || showAllMaterials || showAllServices) {
      temp = temp.filter((t) => {
        const matchesMaterial = showAllMaterials
          ? t.material && t.material.trim() !== "" // Show any ticket with material
          : materialFilter
            ? t.material?.toLowerCase().includes(materialFilter.toLowerCase())
            : false;

        const matchesService = showAllServices
          ? t.service && t.service.trim() !== "" // Show any ticket with service
          : serviceFilter
            ? t.service?.toLowerCase().includes(serviceFilter.toLowerCase())
            : false;

        // OR logic: show if either matches
        return matchesMaterial || matchesService;
      });
    }

    // Filter by location
    if (locationFilter) {
      temp = temp.filter((t) =>
        t.location?.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    // Date filtering
    if (dateRange !== "all") {
      if (dateRange === "custom" && customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);


        temp = temp.filter((t) => {
          const ticketDate = new Date(t.createdAt);
          return ticketDate >= start && ticketDate <= end;
        });
      } else if (dateRange !== "custom") {
        const range = getDateRange(dateRange);
        if (range.start && range.end) {
          const start = new Date(range.start);
          const end = new Date(range.end);

          temp = temp.filter((t) => {
            const ticketDate = new Date(t.createdAt);
            return ticketDate >= start && ticketDate <= end;
          });
        }
      }
    }

    // Search across multiple fields
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      temp = temp.filter(
        (t) =>
          (t.employeeName && t.employeeName.toLowerCase().includes(searchLower)) ||
          (t.subject && t.subject.toLowerCase().includes(searchLower)) ||
          (t.department && t.department.toLowerCase().includes(searchLower)) ||
          (t.customerName && t.customerName.toLowerCase().includes(searchLower)) ||
          (t.description && t.description.toLowerCase().includes(searchLower))
      );
    }

    // Sort
    temp = temp.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime() || 0;
      const dateB = new Date(b.createdAt).getTime() || 0;
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    setFilteredTickets(temp);
  }, [
    selectedDepartment,
    searchTerm,
    sortOrder,
    tickets,
    activeTab,
    statusFilter,
    materialFilter,
    serviceFilter,
    locationFilter,
    dateRange,
    customStartDate,
    customEndDate,
    showAllMaterials,
    showAllServices,
  ]);

  // Get unique values for filter dropdowns
  const uniqueStatuses = [...new Set(tickets.map(t => t.status).filter(Boolean))];
  const uniqueMaterials = [...new Set(tickets.map(t => t.material).filter(Boolean))];
  const uniqueServices = [...new Set(tickets.map(t => t.service).filter(Boolean))];
  const uniqueLocations = [...new Set(tickets.map(t => t.location).filter(Boolean))];
  const uniqueDepartments = [...new Set(tickets.map((t) => t.department))].filter(Boolean);

  const handleAssignChange = (ticketId, field, value) => {
    setAssignData((prev) => ({
      ...prev,
      [ticketId]: {
        ...prev[ticketId],
        [field]: value,
      },
    }));
  };

  const handleAssignTicket = async (ticketId, isReassign = false, oldAssignedTo = null) => {
    const data = assignData[ticketId];
    if (!data?.employeeId) {
      setNotification({ message: "Please select a department and employee to assign.", type: "error" });
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const now = new Date().toISOString();
      const newEmployeeId = data.employeeId;

      // Find the current ticket from state
      const currentTicket = tickets.find(t => t._id === ticketId);
      if (!currentTicket) throw new Error("Ticket not found");

      // Prepare updated assigneeHistory array for reassignment
      let updatedAssigneeHistory = currentTicket.assigneeHistory ? [...currentTicket.assigneeHistory] : [];

      if (isReassign) {
        // Append old assignee to history with timestamp
        updatedAssigneeHistory.push({
          employeeId: oldAssignedTo,
          assignedAt: now,
        });
      }

      // Get the priority from assignData or keep the current one
      const updatedPriority = data.priority || currentTicket.priority;

      const updateBody = {
        updatedAt: now,
        fieldWorkAssigned: data.fieldWorkAssigned || false,
        status: isReassign ? "reassigned" : "assigned",
        assignedTo: newEmployeeId,
        priority: updatedPriority, // Include priority update
        assigneeHistory: isReassign ? updatedAssigneeHistory : currentTicket.assigneeHistory || [],
      };

      // Remove undefined fields
      Object.keys(updateBody).forEach(key => updateBody[key] === undefined && delete updateBody[key]);

      // Step 1: Always hit reassignment endpoint to ensure notification
      try {
        const reassignBody = {
          newAssignedTo: newEmployeeId
        };
        await fetch(
          `${API_BASE_URL}/support-ticket/${ticketId}/reassign`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(reassignBody),
          }
        );
        try {
          await authApi.post('/notifications/bulk', {
            recipients: [newEmployeeId],
            type: 'ticket',
            title: isReassign ? 'Support Ticket Reassigned' : 'Support Ticket Assigned',
            message: `${isReassign ? 'Ticket reassigned' : 'New support ticket assigned'}: ${currentTicket.subject || 'Ticket'}`
          });
          window.dispatchEvent(new Event('ticket-assigned'));
        } catch { }
      } catch (e) {
        // Non-blocking for downstream update
        console.warn('Reassign notification call failed:', e?.message || e);
      }

      const res = await fetch(
        `${API_BASE_URL}/support-ticket/${ticketId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateBody),
        }
      );

      if (!res.ok) throw new Error(isReassign ? "Failed to reassign ticket" : "Failed to assign ticket");

      const message = isReassign ? "Ticket reassigned successfully ✅" : "Ticket assigned successfully ✅";
      setNotification({ message, type: "success" });

      setAssignData((prev) => {
        const newData = { ...prev };
        delete newData[ticketId];
        return newData;
      });

      await fetchTickets(); // Refetch to update
    } catch (err) {
      console.error(err);
      const errorMsg = isReassign ? "Error reassigning ticket ❌" : "Error assigning ticket ❌";
      setNotification({ message: errorMsg, type: "error" });
    }
  };





  const clearAllFilters = () => {
    setSelectedDepartment("");
    setStatusFilter("");
    setMaterialFilter("");
    setServiceFilter("");
    setLocationFilter("");
    setDateRange("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setSearchTerm("");
    setShowDatePicker(false);
    setShowAllMaterials(false);
    setShowAllServices(false);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <motion.div
          className="relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
            aria-label="Loading spinner"
            role="status"
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          />
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <span className="text-blue-600 font-medium">Loading tickets...</span>
          </motion.div>
        </motion.div>
      </div>
    );

  if (error)
    return (
      <motion.div
        className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4">
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="text-center"
          >
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Error</h2>
            <p className="text-gray-700">{error}</p>
            <motion.button
              onClick={fetchTickets}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-2 sm:px-4 lg:px-4">
      <motion.div
        className="max-w-[98%] mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <h1 className="text-4xl font-extrabold text-gray-900 mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 px-1 py-1">
          Support Ticket Dashboard
        </h1>

        {/* Enhanced Filters Section */}
        <motion.div
          className="bg-gradient-to-br from-white to-blue-50/50 rounded-3xl p-6 shadow-xl border border-blue-100/50 mb-8 backdrop-blur-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {/* Header with enhanced styling */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-blue-100">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-800 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Filter & Search Tickets
              </h3>
            </div>
            <motion.button
              onClick={clearAllFilters}
              whileHover={{ scale: 1.05, backgroundColor: "#ef4444" }}
              whileTap={{ scale: 0.95 }}
              className="group text-sm bg-red-100 hover:bg-red-200 text-red-700 font-semibold px-4 py-2 rounded-full flex items-center shadow-md transition-all duration-200 border border-red-200"
            >
              <svg className="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
              Clear All
            </motion.button>
          </div>

          {/* First row of filters with icons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Department Filter */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <label className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Department</label>
              </div>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300 hover:shadow-md"
              >
                <option value="">All Departments</option>
                {uniqueDepartments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              {selectedDepartment && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  Active
                </motion.span>
              )}
            </motion.div>

            {/* Status Filter */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <label className="text-sm font-semibold text-gray-700 group-hover:text-green-600 transition-colors">Status</label>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-green-300 hover:shadow-md"
              >
                <option value="">All Statuses</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              {statusFilter && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  Active
                </motion.span>
              )}
            </motion.div>

            {/* Material Filter */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <label className="text-sm font-semibold text-gray-700 group-hover:text-purple-600 transition-colors">
                    Material
                  </label>
                </div>

                {/* Checkbox for showing all materials */}
                <div className="flex items-center space-x-2">
                  <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAllMaterials}
                      onChange={(e) => {
                        setShowAllMaterials(e.target.checked);
                        if (e.target.checked) {
                          setMaterialFilter(""); // Clear search when showing all
                        }
                      }}
                      className="w-3 h-3 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="ml-1">Show All</span>
                  </label>

                  {materialFilter && (
                    <button
                      onClick={() => setMaterialFilter("")}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={showAllMaterials ? "Showing all materials..." : "Search materials..."}
                  value={materialFilter}
                  onChange={(e) => {
                    setMaterialFilter(e.target.value);
                    if (e.target.value) {
                      setShowAllMaterials(false); // Uncheck when searching
                    }
                  }}
                  disabled={showAllMaterials}
                  className={`w-full pl-12 pr-4 py-4 bg-white border-2 rounded-2xl text-sm shadow-inner focus:outline-none focus:ring-2 transition-all duration-200 placeholder-gray-400 ${showAllMaterials
                    ? "border-green-300 bg-green-50 cursor-not-allowed"
                    : "border-gray-200 hover:border-purple-300 focus:border-purple-500 focus:ring-purple-500 hover:shadow-md"
                    }`}
                />

                {showAllMaterials && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium">
                      All
                    </span>
                  </div>
                )}
              </div>

              {/* Active indicator */}
              {(materialFilter || showAllMaterials) && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  {showAllMaterials ? "All" : "Search"}
                </motion.span>
              )}

              {/* Auto-suggestions (only show when not in "show all" mode) */}
              {materialFilter && !showAllMaterials && uniqueMaterials.filter(material =>
                material.toLowerCase().includes(materialFilter.toLowerCase())
              ).length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                    {uniqueMaterials.filter(material =>
                      material.toLowerCase().includes(materialFilter.toLowerCase())
                    ).slice(0, 5).map((material) => (
                      <div
                        key={material}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => setMaterialFilter(material)}
                      >
                        📦 {material}
                      </div>
                    ))}
                  </div>
                )}
            </motion.div>

            {/* Service Filter - Search Input */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <label className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition-colors">
                    Service
                  </label>
                </div>

                {/* Checkbox for showing all services */}
                <div className="flex items-center space-x-2">
                  <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAllServices}
                      onChange={(e) => {
                        setShowAllServices(e.target.checked);
                        if (e.target.checked) {
                          setServiceFilter(""); // Clear search when showing all
                        }
                      }}
                      className="w-3 h-3 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-1">Show All</span>
                  </label>

                  {serviceFilter && (
                    <button
                      onClick={() => setServiceFilter("")}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={showAllServices ? "Showing all services..." : "Search services..."}
                  value={serviceFilter}
                  onChange={(e) => {
                    setServiceFilter(e.target.value);
                    if (e.target.value) {
                      setShowAllServices(false); // Uncheck when searching
                    }
                  }}
                  disabled={showAllServices}
                  className={`w-full pl-12 pr-4 py-4 bg-white border-2 rounded-2xl text-sm shadow-inner focus:outline-none focus:ring-2 transition-all duration-200 placeholder-gray-400 ${showAllServices
                    ? "border-green-300 bg-green-50 cursor-not-allowed"
                    : "border-gray-200 hover:border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500 hover:shadow-md"
                    }`}
                />

                {showAllServices && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium">
                      All
                    </span>
                  </div>
                )}
              </div>

              {/* Active indicator */}
              {(serviceFilter || showAllServices) && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  {showAllServices ? "All" : "Search"}
                </motion.span>
              )}

              {/* Auto-suggestions (only show when not in "show all" mode) */}
              {serviceFilter && !showAllServices && uniqueServices.filter(service =>
                service.toLowerCase().includes(serviceFilter.toLowerCase())
              ).length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                    {uniqueServices.filter(service =>
                      service.toLowerCase().includes(serviceFilter.toLowerCase())
                    ).slice(0, 5).map((service) => (
                      <div
                        key={service}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => setServiceFilter(service)}
                      >
                        › ï {service}
                      </div>
                    ))}
                  </div>
                )}
            </motion.div>
          </div>

          {/* Second row of filters with icons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Location Filter */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <label className="text-sm font-semibold text-gray-700 group-hover:text-orange-600 transition-colors">Location</label>
              </div>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 hover:border-orange-300 hover:shadow-md"
              >
                <option value="">All Locations</option>
                {uniqueLocations.map((location) => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
              {locationFilter && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  Active
                </motion.span>
              )}
            </motion.div>

            {/* Date Range Filter */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <label className="text-sm font-semibold text-gray-700 group-hover:text-yellow-600 transition-colors">Date Range</label>
              </div>
              <select
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  if (e.target.value === "custom") {
                    setShowDatePicker(true);
                  } else {
                    setShowDatePicker(false);
                  }
                }}
                className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 hover:border-yellow-300 hover:shadow-md"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="weekly">Last 7 Days</option>
                <option value="monthly">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              {dateRange !== "all" && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  Active
                </motion.span>
              )}
            </motion.div>

            {/* Custom Date Picker - Conditional and animated */}
            {showDatePicker && (
              <>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 10 }}
                  transition={{ duration: 0.3 }}
                  className="group relative"
                >
                  <div className="flex items-center mb-2">
                    <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <label className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Start Date</label>
                  </div>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300 hover:shadow-md"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 10 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="group relative"
                >
                  <div className="flex items-center mb-2">
                    <svg className="w-4 h-4 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <label className="text-sm font-semibold text-gray-700 group-hover:text-red-600 transition-colors">End Date</label>
                  </div>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 hover:border-red-300 hover:shadow-md"
                  />
                  {customEndDate && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                    >
                      Set
                    </motion.span>
                  )}
                </motion.div>
              </>
            )}

            {/* Unified Search Bar - Enhanced with modern styling */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative col-span-1 md:col-span-2 lg:col-span-1"
            >
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 mr-2 text-gray-500 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <label className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Search Tickets</label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by name, subject, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border-2 border-gray-200 rounded-2xl text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300 hover:shadow-lg placeholder-gray-400"
                />
              </div>

            </motion.div>

            {/* Sort by Created Date - Enhanced with icon and modern styling */}
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative max-w-md"
            >
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h12M3 16h4m-4 0l4 4m-4-4l4-4" />
                </svg>
                <label className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition-colors">Sort By Date</label>
              </div>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 hover:border-indigo-300 hover:shadow-md"
              >
                <option value="desc">Newest First </option>
                <option value="asc">Oldest First </option>
              </select>
              {sortOrder === "desc" && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded-full shadow-lg"
                >
                  Default
                </motion.span>
              )}
            </motion.div>
          </div>



        </motion.div>

        {/* Tabs and View Mode Toggle */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          {/* Tabs */}
          <motion.div
            className="flex bg-white rounded-2xl p-2 shadow-lg flex-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <button
              onClick={() => setActiveTab("all")}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "all"
                ? "bg-blue-500 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              All Tickets ({filteredTickets.length})
            </button>
            <button
              onClick={() => setActiveTab("assigned")}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "assigned"
                ? "bg-green-500 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              Assigned ({filteredTickets.filter((t) => t.isAssigned).length})
            </button>
            <button
              onClick={() => setActiveTab("unassigned")}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "unassigned"
                ? "bg-orange-500 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              Pending ({filteredTickets.filter((t) => !t.isAssigned).length})
            </button>
          </motion.div>

          {/* View Mode Toggle */}
          <motion.div
            className="bg-white rounded-2xl p-2 shadow-lg flex"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center justify-center py-3 px-6 rounded-xl font-semibold transition-all ${viewMode === "grid"
                ? "bg-blue-500 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Grid View
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center justify-center py-3 px-6 rounded-xl font-semibold transition-all ${viewMode === "list"
                ? "bg-blue-500 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List View
            </button>
          </motion.div>
        </div>

        {/* Ticket Status Distribution Pie Chart */}
        <motion.div
          className="bg-white rounded-2xl p-6 shadow-lg mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
            Ticket Status Distribution
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pie Chart */}
            <div className="h-80 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Assigned', value: filteredTickets.filter(t => t.isAssigned).length },
                      { name: 'Pending', value: filteredTickets.filter(t => !t.isAssigned).length }
                    ].filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#10b981" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} tickets`, name]}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend and Stats */}
            <div className="flex flex-col justify-center space-y-6">
              <div className="space-y-4">
                {/* All Tickets */}
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full bg-blue-500 mr-3"></div>
                    <span className="text-lg font-semibold text-gray-800">All Tickets</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{filteredTickets.length}</span>
                </div>

                {/* Assigned */}
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border-2 border-green-200">
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full bg-green-500 mr-3"></div>
                    <span className="text-lg font-semibold text-gray-800">Assigned</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">
                      {filteredTickets.filter(t => t.isAssigned).length}
                    </div>
                    <div className="text-sm text-green-700">
                      {filteredTickets.length > 0
                        ? ((filteredTickets.filter(t => t.isAssigned).length / filteredTickets.length) * 100).toFixed(1)
                        : 0}%
                    </div>
                  </div>
                </div>

                {/* Pending */}
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border-2 border-orange-200">
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full bg-orange-500 mr-3"></div>
                    <span className="text-lg font-semibold text-gray-800">Pending</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600">
                      {filteredTickets.filter(t => !t.isAssigned).length}
                    </div>
                    <div className="text-sm text-orange-700">
                      {filteredTickets.length > 0
                        ? ((filteredTickets.filter(t => !t.isAssigned).length / filteredTickets.length) * 100).toFixed(1)
                        : 0}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tickets Display */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {filteredTickets.length === 0 ? (
            <motion.div
              className="bg-white rounded-2xl p-8 text-center shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg">No tickets found matching your criteria.</p>
              <button
                onClick={clearAllFilters}
                className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear all filters
              </button>
            </motion.div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredTickets.map((ticket, index) => (
                  <TicketCard
                    key={ticket._id}
                    ticket={ticket}
                    employees={employees}
                    departments={departments}
                    companyId={companyId}
                    assignData={assignData}
                    handleAssignChange={handleAssignChange}
                    handleAssignTicket={handleAssignTicket}
                    fetchTickets={fetchTickets} // Pass for re-open refetch
                    setNotification={setNotification} // Pass for notifications
                    index={index}
                    viewMode={viewMode}
                  />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {filteredTickets.map((ticket, index) => (
                  <TicketCard
                    key={ticket._id}
                    ticket={ticket}
                    employees={employees}
                    departments={departments}
                    companyId={companyId}
                    assignData={assignData}
                    handleAssignChange={handleAssignChange}
                    handleAssignTicket={handleAssignTicket}
                    fetchTickets={fetchTickets} // Pass for re-open refetch
                    setNotification={setNotification} // Pass for notifications
                    index={index}
                    viewMode={viewMode}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.section>
      </motion.div>

      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function TicketCard({
  ticket,
  employees,
  departments,
  companyId,
  assignData,
  handleAssignChange,
  handleAssignTicket,
  fetchTickets, // New prop for re-open refetch
  setNotification, // New prop for notifications
  index,
  viewMode
}) {
  const [expanded, setExpanded] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [showReopenForm, setShowReopenForm] = useState(false); // New: for re-open form
  const [reopenReason, setReopenReason] = useState(""); // New: reason input
  const [reopenError, setReopenError] = useState(""); // New: form validation error


  // New: Reassignment states
  const [reassignDescription, setReassignDescription] = useState("");
  const [showReassignForm, setShowReassignForm] = useState(false);
  const [reassignError, setReassignError] = useState("");



  // Updated: Simplified reassign trigger without reason
  const triggerReassign = () => {
    // Pass oldAssignedTo (current ticket.assignedTo) for history push
    handleAssignTicket(ticket._id, true, ticket.assignedTo);
    setShowReassignForm(false);
  };




  // Get selected department for this ticket's assignment
  const selectedDeptId = assignData[ticket._id]?.selectedDeptId || "";

  // Fixed Employee Filtering Logic (works for array or single deptId)
  const filteredEmployees = employees.filter((emp) => {
    if (!selectedDeptId) return false;

    const empCompany = String(emp.company || "").trim();
    const currentCompany = String(companyId || "").trim();
    const selectedDept = String(selectedDeptId).trim();

    // Normalize department field
    if (!emp.department) return false;

    let empDepts = [];
    if (Array.isArray(emp.department)) {
      empDepts = emp.department.map((d) => String(d).trim());
    } else {
      empDepts = [String(emp.department).trim()];
    }

    // Check if employee belongs to selected dept + company
    return empDepts.includes(selectedDept) && empCompany === currentCompany;
  });

  // New: Handle Re-open Ticket (only changes status and reason; preserves all assignment data)
  const handleReopenTicket = async () => {
    if (reopenReason.trim().length < 10) {
      setReopenError("Reason must be at least 10 characters long.");
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `https://slateblue-mallard-430836.hostingersite.com/api/support-ticket/${ticket._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: "reopen",
            reopenReason: reopenReason.trim(),
            updatedAt: new Date().toISOString(), // Timestamp the reopen
            // Note: Does NOT touch assignedTo, assignedDate, priority, department, fieldWorkAssigned, etc.—preserves all
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to re-open ticket");

      setNotification({ message: "Ticket re-opened successfully ✅", type: "success" });
      setShowReopenForm(false);
      setReopenReason("");
      setReopenError("");
      await fetchTickets(); // Refetch to update the list (preserves assignment data)
    } catch (err) {
      console.error(err);
      setNotification({ message: "Error re-opening ticket ❌", type: "error" });
      setReopenError("Failed to re-open ticket. Please try again.");
    }
  };


  // New: Check if ticket can be re-opened (closed or resolved)
  const canReopen = ticket.status?.toLowerCase() === "closed" || ticket.status?.toLowerCase() === "resolved" || ticket.status?.toLowerCase() === "completed";

  // Image gallery component (unchanged)
  const ImageGallery = ({ images }) => {
    const [selectedImage, setSelectedImage] = useState(null);

    if (!images || images.length === 0) return null;

    return (
      <>
        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
          {images.map((imgUrl, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer"
              onClick={() => setSelectedImage(imgUrl)}
            >
              <img
                src={imgUrl}
                alt={`Ticket image ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden text-center text-xs text-gray-500 mt-1">
                Image not available
              </div>
            </motion.div>
          ))}
        </div>

        {/* Image Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
              onKeyDown={(e) => e.key === "Escape" && setSelectedImage(null)}
              tabIndex={-1}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage}
                  alt="Enlarged view"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
                >
                  ✅•
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  };

  // Shared content for both views (to avoid duplication)
  const SharedTicketContent = () => (
    <>
      {/* Ticket Header */}
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-xl font-semibold text-gray-900 truncate pr-2">{ticket.subject}</h3>
        <motion.button
          whileHover={{ rotate: 90 }}
          transition={{ duration: 0.2 }}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Refresh ticket"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </motion.button>
      </div>

      {/* Ticket Description */}
      <p
        className="text-gray-700 mb-6 line-clamp-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {ticket.description}
      </p>

      {/* Ticket Images */}
      {ticket.image && ticket.image.length > 0 && (
        <div
          className="flex flex-wrap gap-3 mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
        >
          {ticket.image.slice(0, 2).map((imgUrl, i) => (
            <motion.a
              key={i}
              href={imgUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="block w-24 h-24 rounded-lg overflow-hidden border border-gray-200 shadow-sm"
            >
              <img
                src={imgUrl}
                alt={`Ticket ${ticket._id} - image ${i + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => (e.target.style.display = "none")}
              />
            </motion.a>
          ))}
          {ticket.image.length > 2 && (
            <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
              +{ticket.image.length - 2} more
            </div>
          )}
        </div>
      )}

      {/* Badges */}
      <div
        className="flex flex-wrap gap-2 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <StatusBadge status={ticket.status} />
        <PriorityBadge priority={ticket.priority} />
        <span className="inline-flex items-center bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-semibold">
          {ticket.department}
        </span>


      </div>






      <div className="mb-3 text-sm text-gray-700">
        {ticket.customerName && (
          <p><span className="font-medium">Customer:</span> {ticket.customerName}</p>
        )}
        {ticket.customerMobile && (
          <p><span className="font-medium">ž Mobile:</span> {ticket.customerMobile}</p>
        )}
        {ticket.service && (
          <p><span className="font-medium">› ï Service:</span> {ticket.service}</p>
        )}
        {ticket.material && (
          <p><span className="font-medium">📦 Material:</span> {ticket.material}</p>
        )}
        {ticket.location && (
          <p><span className="font-medium">Location:</span> {ticket.location}</p>
        )}

        {ticket.employeeName && (
          <p><span className="font-medium">Raised By:</span> {ticket.employeeName}</p>
        )}

        {ticket.createdAt && (
          <p><span className="font-medium">—ï Created At:</span> {formatDate(ticket.createdAt)}</p>
        )}

        {ticket.updatedAt && (
          <p><span className="font-medium"> Updated At:</span> {formatDate(ticket.updatedAt)}</p>
        )}

      </div>

      {/* Customer Info */}
      {/* <motion.div 
        className="text-sm text-gray-600 mb-4 flex items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        <strong className="mr-1">Raised By:</strong> {ticket.employeeName}
      </motion.div> */}

      {/* Created At */}
      {/* <motion.div 
        className="text-sm text-gray-600 mb-4 flex items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <strong className="mr-1">Created:</strong> {formatDate(ticket.createdAt)}
      </motion.div> */}

      {/* Assignment status - Updated for assigned/reassigned */}
      {/* Assignment status - Updated to show original + reassign history (preserves all data) */}
      <div
        className="text-sm text-gray-600 mb-4 flex items-start"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <svg className="w-4 h-4 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <div>
          <strong className="mr-1">Assignment:</strong>{" "}
          {ticket.isAssigned ? (
            <span className="text-green-600 font-semibold flex flex-col space-y-0.5">
              {/* Current Assignee (only from assignedTo) */}
              <span>✅ Assigned to: {ticket.assignedEmployeeName}</span>
              <span>Field Work: {ticket.fieldWorkAssigned ? "Yes" : "No"}</span>

              {/* Past Assignee History (filter out current to avoid duplication) */}
              {ticket.assigneeHistory && ticket.assigneeHistory.length > 0 && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                  <span className="font-medium text-gray-800">Past Assignees:</span>
                  {ticket.assigneeHistory
                    .filter((history) => history.employeeId !== ticket.assignedTo) // Exclude current
                    .map((history, idx) => (
                      <span key={idx} className="block text-gray-600">
                        📋 {history.employeeName} (assigned on {history.assignedAtFormatted})
                      </span>
                    ))}
                  {ticket.assigneeHistory.filter((history) => history.employeeId !== ticket.assignedTo).length === 0 && (
                    <span className="block text-gray-500 italic">No past assignees</span>
                  )}
                </div>
              )}

              {/* Reopen display (unchanged) */}
              {ticket.status?.toLowerCase() === "reopen" && ticket.reopenReason && (
                <span className="text-xs text-blue-600 bg-blue-50 p-1 rounded mt-1 block">
                  🔓 Reopened: {ticket.reopenReason} {ticket.updatedAt && `(on ${formatDate(ticket.updatedAt)})`}
                </span>
              )}

              {/* Reassign description (if backend stores it) */}
              {ticket.reassignDescription && (
                <span className="text-xs text-yellow-600 bg-yellow-50 p-1 rounded mt-1 block">
                  „ Reassign Reason: {ticket.reassignDescription}
                </span>
              )}
            </span>
          ) : (
            <span className="text-orange-600 font-semibold"> Not Assigned</span>
          )}
        </div>
      </div>




    </>
  );

  if (viewMode === "list") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
        className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100"
      >
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            {/* Left side - Ticket info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h3 className="text-xl font-semibold text-gray-900 truncate">{ticket.subject}</h3>
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
                <span className="inline-flex items-center bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-semibold">
                  {ticket.department}
                </span>
              </div>

              <p className="text-gray-700 mb-3 line-clamp-2">{ticket.description}</p>

              {/* Customer Details Section */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2 gap-2 md:gap-4">
                  <h4 className="font-semibold text-blue-800 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                    Customer Details
                  </h4>
                  {ticket.location && (
                    <div className="text-sm md:text-base">
                      <span className="font-medium text-red-700">Location:</span>
                      <span className="text-gray-900 ml-1">{ticket.location}</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {ticket.customerName && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 w-24">Name:</span>
                      <span className="text-gray-900">{ticket.customerName}</span>
                    </div>
                  )}
                  {ticket.customerMobile && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 w-24">Mobile:</span>
                      <span className="text-gray-900">{ticket.customerMobile}</span>
                    </div>
                  )}
                  {ticket.service && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 w-24">Service:</span>
                      <span className="text-gray-900">{ticket.service}</span>
                    </div>
                  )}
                  {ticket.material && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 w-24">Material:</span>
                      <span className="text-gray-900">{ticket.material}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Images Section */}
              {ticket.image && ticket.image.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                      Attached Images ({ticket.image.length})
                    </h4>
                    <motion.button
                      onClick={() => setShowImages(!showImages)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                    >
                      {showImages ? 'Hide Images' : 'Show Images'}
                      <motion.span
                        animate={{ rotate: showImages ? 180 : 0 }}
                        className="ml-1"
                      >
                        –
                      </motion.span>
                    </motion.button>
                  </div>

                  <AnimatePresence>
                    {showImages && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ImageGallery images={ticket.image} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Shared content for list view */}
              {/* <SharedTicketContent /> */}
            </div>

            {/* Right side - Actions */}
            <div className="flex flex-col gap-3 lg:items-end">
              <motion.button
                onClick={() => setExpanded(!expanded)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="py-2 px-4 bg-blue-100 text-blue-700 rounded-xl font-semibold text-sm flex items-center"
              >
                {expanded ? "Hide Options" : "Show Options"}
                <motion.span
                  animate={{ rotate: expanded ? 180 : 0 }}
                  className="ml-2"
                >
                  –
                </motion.span>
              </motion.button>
            </div>
          </div>

          {/* Expanded assignment options - Updated with Re-open */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6 pt-6 border-t border-gray-200 space-y-4"
              >
                <h4 className="font-semibold text-gray-800 text-lg">Assignment Options</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={assignData[ticket._id]?.priority ?? ticket.priority ?? ""}
                      onChange={(e) => handleAssignChange(ticket._id, "priority", e.target.value)}
                      className="w-full border border-gray-300 rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    >
                      <option value="">-- Change Priority --</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </motion.div>

                  {/* Department Selection */}
                  <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Department</label>
                    <select
                      value={selectedDeptId}
                      onChange={(e) => {
                        handleAssignChange(ticket._id, "selectedDeptId", e.target.value);
                        handleAssignChange(ticket._id, "employeeId", "");
                      }}
                      className="w-full border border-gray-300 rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    >
                      <option value="">-- Select Department --</option>
                      {departments
                        .filter((dept) => String(dept.company) === String(companyId))
                        .map((dept) => (
                          <option key={dept._id} value={String(dept._id)}>
                            {dept.name}
                          </option>
                        ))}
                    </select>
                  </motion.div>

                  {/* Employee Selection */}
                  <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign To Employee</label>
                    <select
                      value={assignData[ticket._id]?.employeeId ?? ""}
                      onChange={(e) => handleAssignChange(ticket._id, "employeeId", e.target.value)}
                      disabled={!selectedDeptId || employees.length === 0}
                      className={`w-full border rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all ${!selectedDeptId || employees.length === 0
                        ? "border-gray-300 bg-gray-100 cursor-not-allowed"
                        : "border-gray-300 focus:ring-blue-400 focus:border-transparent"
                        }`}
                    >
                      <option value="">-- Select Employee --</option>
                      {employees.length === 0 ? (
                        <option disabled>Loading employees...</option>
                      ) : filteredEmployees.length > 0 ? (
                        filteredEmployees.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {emp.teamMemberName}
                          </option>
                        ))
                      ) : selectedDeptId ? (
                        <option disabled>No employees in this department</option>
                      ) : (
                        <option disabled>Select department first</option>
                      )}
                    </select>
                  </motion.div>

                  <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Field Work</label>
                    <select
                      value={assignData[ticket._id]?.fieldWorkAssigned ?? ticket.fieldWorkAssigned ?? false}
                      onChange={(e) => handleAssignChange(ticket._id, "fieldWorkAssigned", e.target.value === "true")}
                      className="w-full border border-gray-300 rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    >
                      <option value="false">No Field Work Required</option>
                      <option value="true">Field Work Required</option>
                    </select>
                  </motion.div>
                </div>

                {/* Updated: Main Assign/Reassign Button + Form */}
                {!["resolved", "closed", "completed"].includes(ticket.status?.toLowerCase()) && (
                  <motion.button
                    onClick={() => {
                      if (ticket.isAssigned) {
                        // Direct reassign without form
                        handleAssignTicket(ticket._id, true, ticket.assignedTo);
                      } else {
                        handleAssignTicket(ticket._id, false); // Initial assign
                      }
                    }}
                    whileHover={{ scale: 1.02, boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)" }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!assignData[ticket._id]?.employeeId}
                    className={`w-full py-3 px-6 rounded-xl text-white font-semibold shadow-md transition-all ${assignData[ticket._id]?.employeeId
                      ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                      : "bg-gradient-to-r from-gray-400 to-gray-500 cursor-not-allowed"
                      }`}
                  >
                    {ticket.isAssigned ? (showReassignForm ? "Confirm Reassign" : "Re-Assign Ticket") : "Assign Ticket"}
                  </motion.button>
                )}


                {/* New: Reassign Form (only if already assigned) */}
                {ticket.isAssigned && (
                  <AnimatePresence>
                    {showReassignForm && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 space-y-3 p-4 bg-yellow-50 rounded-xl border border-yellow-200"
                      >
                        <h5 className="font-semibold text-yellow-800">Reassignment Reason</h5>
                        <textarea
                          value={reassignDescription}
                          onChange={(e) => {
                            setReassignDescription(e.target.value);
                            setReassignError("");
                          }}
                          placeholder="Enter reason for reassignment (min 10 characters)..."
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-vertical min-h-[80px] text-sm"
                        />
                        {reassignError && <p className="text-red-600 text-xs">{reassignError}</p>}
                        <div className="flex gap-2 justify-end">
                          <motion.button
                            onClick={() => {
                              setShowReassignForm(false);
                              setReassignDescription("");
                              setReassignError("");
                            }}
                            whileHover={{ scale: 1.02 }}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </motion.button>
                          <motion.button
                            onClick={triggerReassign}
                            whileHover={{ scale: 1.02 }}
                            className="px-4 py-2 text-white bg-yellow-600 rounded-lg hover:bg-yellow-700"
                            disabled={reassignDescription.trim().length < 10}
                          >
                            Confirm Reassign
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}


                {/* New: Re-open Ticket Button and Form (only for closed/resolved) */}
                {canReopen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 pt-4 border-t border-gray-200"
                  >
                    <motion.button
                      onClick={() => setShowReopenForm(!showReopenForm)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 px-6 rounded-xl text-white font-semibold shadow-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-all"
                      aria-label="Re-open this ticket"
                    >
                      🔓 Repeat
                    </motion.button>

                    <AnimatePresence>
                      {showReopenForm && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, scale: 0.95 }}
                          animate={{ opacity: 1, height: "auto", scale: 1 }}
                          exit={{ opacity: 0, height: 0, scale: 0.95 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4 space-y-3 p-4 bg-blue-50 rounded-xl border border-blue-200"
                        >
                          <h5 className="font-semibold text-blue-800">Why are you re-opening this ticket?</h5>
                          <textarea
                            value={reopenReason}
                            onChange={(e) => {
                              setReopenReason(e.target.value);
                              setReopenError(""); // Clear error on input
                            }}
                            placeholder="Enter reason for re-opening (min 10 characters)..."
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical min-h-[80px] text-sm"
                            aria-label="Re-open reason"
                          />
                          {reopenError && (
                            <p className="text-red-600 text-xs">{reopenError}</p>
                          )}
                          <div className="flex gap-2 justify-end">
                            <motion.button
                              onClick={() => {
                                setShowReopenForm(false);
                                setReopenReason("");
                                setReopenError("");
                              }}
                              whileHover={{ scale: 1.02 }}
                              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </motion.button>
                            <motion.button
                              onClick={handleReopenTicket}
                              whileHover={{ scale: 1.02 }}
                              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Confirm Re-open
                            </motion.button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  }

  // Grid View (condensed layout with shared content)
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{
        y: -5,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
      }}
      className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 flex flex-col"
    >
      <div className="p-6 flex-1">
        {/* Shared content for grid view */}
        <SharedTicketContent />
      </div>

      {/* Assignment Controls - Updated with Re-open */}
      <motion.div
        className="bg-gray-50 p-6 border-t border-gray-200 space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <motion.button
          onClick={() => setExpanded(!expanded)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-2 bg-gray-200 rounded-xl text-gray-700 font-semibold flex items-center justify-center"
        >
          {expanded ? "Hide Options" : "Show Assignment Options"}
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            className="ml-2"
          >
            –
          </motion.span>
        </motion.button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4 overflow-hidden"
            >
              <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={assignData[ticket._id]?.priority ?? ticket.priority ?? ""}
                  onChange={(e) => handleAssignChange(ticket._id, "priority", e.target.value)}
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  aria-label={`Select priority for ticket ${ticket.subject}`}
                >
                  <option value="">-- Change Priority --</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </motion.div>

              {/* Department Selection for Assignment */}
              <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Department</label>
                <select
                  value={selectedDeptId}
                  onChange={(e) => {
                    handleAssignChange(ticket._id, "selectedDeptId", e.target.value);
                    handleAssignChange(ticket._id, "employeeId", "");
                  }}
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                >
                  <option value="">-- Select Department --</option>
                  {departments
                    .filter((dept) => String(dept.company) === String(companyId))
                    .map((dept) => (
                      <option key={dept._id} value={String(dept._id)}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </motion.div>

              {/* Employee Selection based on selected department */}
              <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To Employee</label>
                <select
                  value={assignData[ticket._id]?.employeeId ?? ""}
                  onChange={(e) => handleAssignChange(ticket._id, "employeeId", e.target.value)}
                  disabled={!selectedDeptId || employees.length === 0}
                  className={`w-full border rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all ${!selectedDeptId || employees.length === 0
                    ? "border-gray-300 bg-gray-100 cursor-not-allowed"
                    : "border-gray-300 focus:ring-blue-400 focus:border-transparent"
                    }`}
                >
                  <option value="">-- Select Employee --</option>
                  {employees.length === 0 ? (
                    <option disabled>Loading employees...</option>
                  ) : filteredEmployees.length > 0 ? (
                    filteredEmployees.map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.teamMemberName}
                      </option>
                    ))
                  ) : selectedDeptId ? (
                    <option disabled>No employees in this department</option>
                  ) : (
                    <option disabled>Select department first</option>
                  )}
                </select>
                {!selectedDeptId && (
                  <p className="text-xs text-gray-500 mt-1">Please select a department first</p>
                )}
                {selectedDeptId && filteredEmployees.length === 0 && (
                  <p className="text-xs text-orange-500 mt-1">No employees assigned to this department</p>
                )}
              </motion.div>

              <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Work</label>
                <select
                  value={
                    assignData[ticket._id]?.fieldWorkAssigned !== undefined
                      ? assignData[ticket._id].fieldWorkAssigned
                      : ticket.fieldWorkAssigned ?? false
                  }
                  onChange={(e) =>
                    handleAssignChange(ticket._id, "fieldWorkAssigned", e.target.value === "true")
                  }
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  aria-label={`Select field work option for ticket ${ticket.subject}`}
                >
                  <option value="false">No Field Work Required</option>
                  <option value="true">Field Work Required</option>
                </select>
              </motion.div>

              {/* Updated: Main Assign/Reassign Button + Form */}
              {!["resolved", "closed", "completed"].includes(ticket.status?.toLowerCase()) && (
                <motion.button
                  onClick={() => {
                    if (ticket.isAssigned) {
                      // Direct reassign without form
                      handleAssignTicket(ticket._id, true, ticket.assignedTo);
                    } else {
                      handleAssignTicket(ticket._id, false); // Initial assign
                    }
                  }}
                  whileHover={{ scale: 1.02, boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)" }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!assignData[ticket._id]?.employeeId}
                  className={`w-full py-3 px-6 rounded-xl text-white font-semibold shadow-md transition-all ${assignData[ticket._id]?.employeeId
                    ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                    : "bg-gradient-to-r from-gray-400 to-gray-500 cursor-not-allowed"
                    }`}
                >
                  {ticket.isAssigned ? "Re-Assign Ticket" : "Assign Ticket"}  </motion.button>
              )}


              {/* New: Reassign Form (only if already assigned) */}
              {ticket.isAssigned && (
                <AnimatePresence>
                  {showReassignForm && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 space-y-3 p-4 bg-yellow-50 rounded-xl border border-yellow-200"
                    >
                      <h5 className="font-semibold text-yellow-800">Reassignment Reason</h5>
                      <textarea
                        value={reassignDescription}
                        onChange={(e) => {
                          setReassignDescription(e.target.value);
                          setReassignError("");
                        }}
                        placeholder="Enter reason for reassignment (min 10 characters)..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-vertical min-h-[80px] text-sm"
                      />
                      {reassignError && <p className="text-red-600 text-xs">{reassignError}</p>}
                      <div className="flex gap-2 justify-end">
                        <motion.button
                          onClick={() => {
                            setShowReassignForm(false);
                            setReassignDescription("");
                            setReassignError("");
                          }}
                          whileHover={{ scale: 1.02 }}
                          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          onClick={triggerReassign}
                          whileHover={{ scale: 1.02 }}
                          className="px-4 py-2 text-white bg-yellow-600 rounded-lg hover:bg-yellow-700"
                          disabled={reassignDescription.trim().length < 10}
                        >
                          Confirm Reassign
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}


              {/* New: Re-open Ticket Button and Form (grid view version - only for closed/resolved) */}
              {canReopen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 pt-4 border-t border-gray-200"
                >
                  <motion.button
                    onClick={() => setShowReopenForm(!showReopenForm)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 px-6 rounded-xl text-white font-semibold shadow-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-all"
                    aria-label="Re-open this ticket"
                  >
                    🔓 Repeat
                  </motion.button>

                  <AnimatePresence>
                    {showReopenForm && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, scale: 0.95 }}
                        animate={{ opacity: 1, height: "auto", scale: 1 }}
                        exit={{ opacity: 0, height: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 space-y-3 p-4 bg-blue-50 rounded-xl border border-blue-200"
                      >
                        <h5 className="font-semibold text-blue-800">Why are you re-opening this ticket?</h5>
                        <textarea
                          value={reopenReason}
                          onChange={(e) => {
                            setReopenReason(e.target.value);
                            setReopenError(""); // Clear error on input
                          }}
                          placeholder="Enter reason for re-opening (min 10 characters)..."
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical min-h-[80px] text-sm"
                          aria-label="Re-open reason"
                        />
                        {reopenError && (
                          <p className="text-red-600 text-xs">{reopenError}</p>
                        )}
                        <div className="flex gap-2 justify-end">
                          <motion.button
                            onClick={() => {
                              setShowReopenForm(false);
                              setReopenReason("");
                              setReopenError("");
                            }}
                            whileHover={{ scale: 1.02 }}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </motion.button>
                          <motion.button
                            onClick={handleReopenTicket}
                            whileHover={{ scale: 1.02 }}
                            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Confirm Re-open
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
              {/* Assign Button (already placed above the re-open section) */}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}


export default AssignTickets;


